import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faFileContract,
  faUser,
  faHome,
  faCalendarAlt,
  faMoneyBillWave,
  faCreditCard,
  faCalculator,
  faSignOutAlt,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { tenantService } from '../../services/tenantService';
import { propertyService } from '../../services/propertyService';
import { companyService } from '../../services/companyService';
import { formatCurrency } from '../../utils/currencyFormatter';
import { pdfService } from '../../services/pdfService';

const ContractDetails = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [contract, setContract] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [propertyUnit, setPropertyUnit] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [propertyGroup, setPropertyGroup] = useState(null);

  useEffect(() => {
    fetchContractDetails();
  }, [contractId]);

  const fetchContractDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const contractData = await contractService.getContractById(contractId);
      setContract(contractData);
      
      // Fetch related data
      const [tenantData, unitData, settingsData, companyData] = await Promise.all([
        tenantService.getTenantById(contractData.tenantId),
        propertyService.getPropertyUnitById(contractData.propertyUnitId),
        contractService.getBrokerageSettings(contractData.companyId),
        companyService.getCompanyById(contractData.companyId)
      ]);
      
      setTenant(tenantData);
      setPropertyUnit(unitData);
      // Set logo: custom logo if enabled and available, otherwise use default URProperty logo
      setCompanyLogo((settingsData.logoEnabled && settingsData.logoUrl) ? settingsData.logoUrl : '/urproperty-logo.png');
      setCompanyInfo(companyData);
      
      // Fetch property group if unit has groupId
      if (unitData && unitData.groupId) {
        try {
          const groupData = await propertyService.getPropertyGroupById(unitData.groupId);
          setPropertyGroup(groupData);
        } catch (error) {
          console.error('Error fetching property group:', error);
          // Don't fail the entire request if group fetch fails
        }
      }
    } catch (error) {
      console.error('Error fetching contract details:', error);
      setError('Failed to load contract details: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete contract "${contract?.id}"? This action cannot be undone.`)) {
      navigate('/manager/contracts', { state: { deleteContractId: contractId } });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      expired: { variant: 'danger', text: 'Expired' },
      terminated: { variant: 'secondary', text: 'Terminated' },
      pending: { variant: 'warning', text: 'Pending' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const downloadContract = async () => {
    try {
      // Generate PDF
      const pdf = await pdfService.generateContractPDF(contract, tenant, propertyUnit, companyLogo, companyInfo);
      
      // Download the PDF
      const filename = `contract_${contract?.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdfService.downloadPDF(pdf, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF: ' + (error.message || error));
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faFileContract} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/contracts')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Contracts
        </Button>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Contract not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/contracts')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Contracts
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/manager/contracts')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Contracts
          </Button>
          <div>
            <h2 className="mb-1">Contract Details</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faFileContract} className="me-1" />
              {contract.contractId || contract.id}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to={`/manager/contracts/${contractId}/edit`} variant="primary">
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Contract
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/manager/contracts')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Contracts
          </Button>
          <Button variant="outline-info" onClick={downloadContract}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Export PDF
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Contract
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* Contract Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                Contract Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Contract ID:</strong>
                    <div className="text-muted">{contract.contractId || contract.id}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>{getStatusBadge(contract.status)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Start Date:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>End Date:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Duration:</strong>
                    <div className="text-muted">{contract.contractDuration || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Currency:</strong>
                    <div className="text-muted">{contract.currency || 'USD'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Created:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Last Updated:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                </Col>
              </Row>
              {contract.notes && (
                <div className="mt-3">
                  <strong>Notes:</strong>
                  <div className="text-muted mt-1">{contract.notes}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Financial Details */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-primary me-2" />
                Financial Details
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Total Rental Amount:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      {formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Monthly Rent:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      {formatCurrency(
                        contract.monthlyRent || (contract.totalRentalAmount && contract.contractDuration ? contract.totalRentalAmount / contract.contractDuration : contract.totalRentalAmount / 12), 
                        contract.currency || 'USD'
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Number of Cheques:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCreditCard} className="me-1" />
                      {contract.numberOfCheques || 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Installment Amount:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      {formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD')}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Admin Fees:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalculator} className="me-1" />
                      {formatCurrency(contract.brokerageFee || 0, contract.currency || 'USD')}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Admin Fees Percentage:</strong>
                    <div className="text-muted">{contract.brokeragePercentage || 0}%</div>
                  </div>
                  <div className="mb-3">
                    <strong>Total Amount:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      {formatCurrency(contract.totalAmount || 0, contract.currency || 'USD')}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Tenant Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                Tenant Information
              </h5>
            </Card.Header>
            <Card.Body>
              {tenant ? (
                <>
                  <div className="mb-3">
                    <strong>Name:</strong>
                    <div className="text-muted">{tenant.name}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong>
                    <div className="text-muted">{tenant.email}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Phone:</strong>
                    <div className="text-muted">{tenant.phone}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>
                      <Badge bg={tenant.status === 'active' ? 'success' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => navigate(`/manager/tenants/${tenant.id}`)}
                  >
                    View Tenant Details
                  </Button>
                </>
              ) : (
                <div className="text-muted">Tenant information not available</div>
              )}
            </Card.Body>
          </Card>

          {/* Property Unit Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                Property Unit Information
              </h5>
            </Card.Header>
            <Card.Body>
              {propertyUnit ? (
                <>
                  <div className="mb-3">
                    <strong>Property Group:</strong>
                    <div className="text-muted">
                      {propertyGroup ? propertyGroup.name : 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Unit Number:</strong>
                    <div className="text-muted">{propertyUnit.unitNumber}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Type:</strong>
                    <div className="text-muted">{propertyUnit.unitType}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Bedrooms:</strong>
                    <div className="text-muted">{propertyUnit.bedrooms || 'Studio'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Bathrooms:</strong>
                    <div className="text-muted">{propertyUnit.bathrooms || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>
                      <Badge bg={propertyUnit.status === 'occupied' ? 'warning' : 'success'}>
                        {propertyUnit.status}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => navigate(`/manager/units/${propertyUnit.id}`)}
                  >
                    View Unit Details
                  </Button>
                </>
              ) : (
                <div className="text-muted">Property unit information not available</div>
              )}
            </Card.Body>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary"
                  onClick={() => navigate(`/manager/contracts/${contractId}/edit`)}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Contract
                </Button>
                <Button 
                  variant="outline-primary"
                  onClick={downloadContract}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download Contract
                </Button>
                <Button 
                  variant="outline-danger"
                  onClick={handleDelete}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete Contract
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ContractDetails; 